/**
 * Frontend types + tone helpers for industry-agnostic Output primitives.
 * Backend normalizes; UI maps tone/trend/emphasis → TMOS theme styles.
 */

export type ArtifactTone = 'neutral' | 'positive' | 'negative' | 'warning' | 'info';
export type ArtifactEmphasis = 'normal' | 'high';
export type ArtifactTrend = 'up' | 'down' | 'flat' | 'positive' | 'negative' | 'neutral';
export type ArtifactChartType = 'bar' | 'line' | 'pie';

export type ArtifactBlock =
  | { type: 'heading'; text: string; level?: number }
  | { type: 'text'; text: string }
  | { type: 'callout'; text: string; tone?: ArtifactTone }
  | {
      type: 'metric';
      label: string;
      value: string;
      unit?: string;
      trend?: ArtifactTrend;
      emphasis?: ArtifactEmphasis;
      tone?: ArtifactTone;
    }
  | { type: 'metrics'; items: Array<Extract<ArtifactBlock, { type: 'metric' }>> }
  | {
      type: 'table';
      title?: string;
      columns: string[];
      rows: unknown[][];
      tone?: ArtifactTone;
      row_tones?: Array<ArtifactTone | null>;
    }
  | {
      type: 'chart';
      chart_type: ArtifactChartType;
      title?: string;
      data: Array<{ name: string; value: number }>;
    }
  | {
      type: 'section';
      title: string;
      blocks: ArtifactBlock[];
      tone?: ArtifactTone;
    };

export function asArtifactBlocks(raw: unknown): ArtifactBlock[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((b): b is ArtifactBlock => {
    if (!b || typeof b !== 'object' || Array.isArray(b)) return false;
    const t = (b as { type?: unknown }).type;
    return typeof t === 'string' && t.length > 0;
  });
}

/** Border / surface accents for tone (TMOS CSS variables). */
export function toneBorderClass(tone?: ArtifactTone | null): string {
  switch (tone) {
    case 'positive':
      return 'border-[var(--color-success-border,var(--color-border))] bg-[var(--color-success-bg,#ecfdf5)]';
    case 'negative':
      return 'border-[var(--color-danger-border,var(--color-border))] bg-[var(--color-danger-bg,#fef2f2)]';
    case 'warning':
      return 'border-[var(--color-warning-border,var(--color-border))] bg-[var(--color-warning-bg,#fffbeb)]';
    case 'info':
      return 'border-[var(--color-accent-bg,var(--color-border))] bg-[var(--color-secondary-bg,#eff6ff)]';
    default:
      return 'border-[var(--color-border)] bg-[var(--color-surface-bg)]';
  }
}

export function toneTextClass(tone?: ArtifactTone | null): string {
  switch (tone) {
    case 'positive':
      return 'text-[var(--color-success-text,#047857)]';
    case 'negative':
      return 'text-[var(--color-danger-text,#b91c1c)]';
    case 'warning':
      return 'text-[var(--color-warning-text,#b45309)]';
    case 'info':
      return 'text-[var(--color-primary-font,var(--color-surface-text))]';
    default:
      return 'text-[var(--color-surface-text)]';
  }
}

export function trendLabel(trend?: ArtifactTrend | null): string {
  if (trend === 'up' || trend === 'positive') return '↑';
  if (trend === 'down' || trend === 'negative') return '↓';
  if (trend === 'flat' || trend === 'neutral') return '→';
  return '';
}

export function trendTone(trend?: ArtifactTrend | null): ArtifactTone | undefined {
  if (trend === 'up' || trend === 'positive') return 'positive';
  if (trend === 'down' || trend === 'negative') return 'negative';
  if (trend === 'flat' || trend === 'neutral') return 'neutral';
  return undefined;
}

/** Recharts palette from TMOS-ish blues + accents (not LLM-chosen hex). */
export const CHART_PALETTE = [
  'var(--color-primary-bg, #004C94)',
  'var(--color-secondary-bg, #5B94DD)',
  'var(--color-accent-bg, #0ea5e9)',
  '#047857',
  '#b45309',
  '#7c3aed',
];
